import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Table, type Selection } from '@heroui/react';
import type { BarcodeIssue } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { ProductTableFooter } from '../productTable/ProductTableFooter';
import { BarcodeIssuesRow } from './BarcodeIssuesRow';
import { BarcodeIssuesSkeleton } from './BarcodeIssuesSkeleton';
import {
    BARCODE_ISSUES_COLUMNS,
    BARCODE_ISSUES_SKELETON_KEYS,
} from './barcodeIssuesColumns';
import { barcodeIssueKeyOf } from './barcodeIssuesMeta';
import type { UseBarcodeIssuesResult } from './useBarcodeIssues';

const COPY = 'products.barcodeIssues';

type BarcodeIssuesTableProps = {
    state: UseBarcodeIssuesResult;
    /** `null` while the first page loads: placeholder rows, no footer. */
    rows: BarcodeIssue[] | null;
    totalCount: number;
    /** The rows are the previous page's: nothing can be picked. */
    isPlaceholder: boolean;
};

/**
 * The listed barcodes with a checkbox per row (the HeroUI table's own
 * selection), and the pagination footer once the count is known. The table
 * scrolls sideways on a narrow screen.
 */
export const BarcodeIssuesTable: React.FC<BarcodeIssuesTableProps> = ({
    state,
    rows,
    totalCount,
    isPlaceholder,
}) => {
    const { t } = useTranslation('pages');
    const pageKeys = (rows ?? []).map(barcodeIssueKeyOf);
    const onSelectionChange = (keys: Selection) =>
        state.onSelectionChange(
            keys === 'all' ? new Set(pageKeys) : new Set([...keys].map(String)),
        );
    const disabledKeys =
        rows === null
            ? BARCODE_ISSUES_SKELETON_KEYS
            : isPlaceholder
              ? pageKeys
              : [];

    return (
        <Table>
            <Table.ScrollContainer>
                <Table.Content
                    aria-label={t(`${COPY}.tableAria`)}
                    className="min-w-[1100px] text-sm"
                    disabledKeys={disabledKeys}
                    onSelectionChange={onSelectionChange}
                    selectedKeys={state.selectedKeys}
                    selectionMode="multiple"
                >
                    <Table.Header>
                        <Table.Column className="pe-0" id="select">
                            <Checkbox
                                aria-label={t(`${COPY}.selectAllAria`)}
                                slot="selection"
                                {...getNodeTestIdProp(
                                    'barcode-issues-select-all',
                                )}
                            >
                                <Checkbox.Content>
                                    <Checkbox.Control>
                                        <Checkbox.Indicator />
                                    </Checkbox.Control>
                                </Checkbox.Content>
                            </Checkbox>
                        </Table.Column>
                        {BARCODE_ISSUES_COLUMNS.map((column) => (
                            <Table.Column
                                className={
                                    column === 'stock' ? 'text-end' : undefined
                                }
                                id={column}
                                isRowHeader={column === 'product'}
                                key={column}
                            >
                                {t(`${COPY}.columns.${column}`)}
                            </Table.Column>
                        ))}
                    </Table.Header>
                    <Table.Body>
                        {rows === null ? (
                            <BarcodeIssuesSkeleton />
                        ) : (
                            rows.map((issue) => {
                                const key = barcodeIssueKeyOf(issue);
                                const rowError = state.rowErrors[key];
                                return (
                                    <BarcodeIssuesRow
                                        choice={state.choiceOf(issue)}
                                        errorCode={
                                            rowError?.kind === 'row'
                                                ? rowError.code
                                                : null
                                        }
                                        isDisabled={isPlaceholder}
                                        issue={issue}
                                        key={key}
                                        onFixChange={state.onFixChange}
                                        onTypedChange={state.onTypedChange}
                                        onUseAnyway={state.onUseAnyway}
                                        rowKey={key}
                                    />
                                );
                            })
                        )}
                    </Table.Body>
                </Table.Content>
            </Table.ScrollContainer>
            {rows === null ? null : (
                <Table.Footer>
                    <ProductTableFooter
                        onPageChange={state.onPageChange}
                        onPageSizeChange={state.onPageSizeChange}
                        page={state.page}
                        pageSize={state.pageSize}
                        pageWindow={state.pageWindow}
                        totalCount={totalCount}
                        totalPages={state.totalPages}
                    />
                </Table.Footer>
            )}
        </Table>
    );
};
